import React, { useEffect, useRef } from 'react';
import { Col, Form, Input, InputNumber, Radio, RadioChangeEvent, Row, Switch } from 'antd';
import Button from 'components/Button';
import Text from 'components/Text';
import { styled } from 'styled-components';
import RangeSaleInput from './StaffSalary/RangeSaleInput';
import SalaryPerTypes from './StaffSalary/SalaryPerTypes';
import PayrollTipOptions from './StaffSalary/PayrollTipOptions';
import ButtonGroup from './ButtonGroup';
import {
  ESalaryMode,
  ESalaryPayrollType,
  initSalaryInfo,
} from 'features/settingStaff/services/constants';
import Box from 'components/Box';
import colorTheme from 'constants/color';
import settingStaffSelectors from 'features/settingStaff/services/selectors';
import { ISalaryConfig } from 'features/settingStaff/services/types/staff';
import { useAppDispatch } from 'store/hooks';
import settingStaffActions from 'features/settingStaff/services/actions';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import { createPerTypeHandlers } from './StaffSalary/utils';
import SwitchV2 from 'components/SwitchV2';
import CleaningFee from './StaffSalary/CleaningFee';

type Props = {
  v2?: boolean;
};
const StaffSalary: React.FC<Props> = ({ v2 }) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const staffSelectedSalary =
    settingStaffSelectors.selectedStaffData.dataSalary();
  const salaryMode = Form.useWatch('salaryType', form) as ESalaryMode;
  const payrollType =
    Form.useWatch('payrollType', form) || ESalaryPayrollType.DEFAULT;

  const defaultValues = useRef({ perTypeCash: 0, perTypeCheck: 100 });

  const { handlePerTypeCashChange, handlePerTypeCheckChange } = createPerTypeHandlers(form);

  useEffect(() => {
    form.setFieldsValue({
      perTypeCheck: defaultValues.current.perTypeCheck,
      perTypeCash: defaultValues.current.perTypeCash
    });
  }, [payrollType, form]);


  const onFinish = async (values: ISalaryConfig) => {
    const payload = {
      ...values,
      staffId: staffSelectedSalary.staffId,
      payrollType: values.payrollType,
      federalTax: values.federalTax,
      socialSecurityTax: values.socialSecurityTax,
      medicalTax: values.medicalTax,
      saleChargeFee: values.saleChargeFee,
      payrollTypeCash: +values.payrollTypeCash,
      payrollTypeCheck: +values.payrollTypeCheck,
      perTypeCash: +values.perTypeCash,
      perTypeCheck: +values.perTypeCheck,
      maxHoursWeekly: values.maxHoursWeekly,
      overtimeValue: values.overtimeValue,
      enableCleaningFee: values.enableCleaningFee,
      cleaningFee: values.cleaningFee,
      cleanerPercentCash: values.cleanerPercentCash,
      cleanerPercentCheck: +values.cleanerPercentCheck,
      minSaleByDay: values.minSaleByDay,
    };
    dispatch(settingStaffActions.staffUpdateSalary.fetch(payload));
  };

  useEffect(() => {
    if (staffSelectedSalary) {
      defaultValues.current = {
        perTypeCash: staffSelectedSalary.perTypeCash ?? 0,
        perTypeCheck: staffSelectedSalary.perTypeCheck ?? 100
      };

      form.setFieldsValue({
        ...staffSelectedSalary,
        tipFeeType: staffSelectedSalary.tipFeeType ?? 'PERCENT',
        tipFeeValue: staffSelectedSalary.tipFeeValue ?? 0,
        enableMinComm: staffSelectedSalary.enableMinComm ?? false,
        minCommValue: staffSelectedSalary.minCommValue ?? 0,
        payrollType: [ESalaryPayrollType.DEFAULT, ESalaryPayrollType.W2].includes(staffSelectedSalary.payrollType) ? staffSelectedSalary.payrollType : ESalaryPayrollType.DEFAULT,
        federalTax: staffSelectedSalary.federalTax,
        socialSecurityTax: staffSelectedSalary.socialSecurityTax,
        medicalTax: staffSelectedSalary.medicalTax,
        saleChargeFee: staffSelectedSalary.saleChargeFee ?? 0,
        fee: staffSelectedSalary.fee ?? 0,
        perTypeCash: staffSelectedSalary.perTypeCash ?? 0,
        perTypeCheck: staffSelectedSalary.perTypeCheck ?? 100,
        enableCleaningFee: staffSelectedSalary.enableCleaningFee ?? false,
        cleaningFee: staffSelectedSalary.cleaningFee ?? 0,
        cleanerPercentCash: staffSelectedSalary.cleanerPercentCash ?? 0,
        cleanerPercentCheck: staffSelectedSalary.cleanerPercentCheck ?? 100,
        minSaleByDay: staffSelectedSalary.minSaleByDay ?? 0,
      });
    }
  }, [staffSelectedSalary]);

  useEffect(() => {
    if (staffSelectedSalary) {
      if (salaryMode === ESalaryMode.HOURS) {
        form.setFieldsValue({ hourlyValue: staffSelectedSalary.hourlyValue });
      }
      if (salaryMode === ESalaryMode.COMMISSION_ACCELERATE) {
        form.setFieldsValue({
          salaryAccelerateOption: staffSelectedSalary.salaryAccelerateOption,
        });
      }
    }
  }, [salaryMode]);

  useEffect(() => {
    if (salaryMode === ESalaryMode.FIX_SALARY) {
      const current = form.getFieldValue('supplyShareType');
      if (!current) {
        form.setFieldsValue({ supplyShareType: 'MONEY' });
      }
    }
  }, [salaryMode, form]);

  const defaultSupplyShareType = useRef(staffSelectedSalary?.supplyShareType || 'MONEY');
  const defaultSupplyFee = useRef(staffSelectedSalary?.supplyFee ?? 0);
  const defaultSupplySale = useRef(staffSelectedSalary?.supplySale ?? 0);

  const handleSupplyShareTypeChange = (e: RadioChangeEvent) => {
    const newType = e.target.value;
    if (newType === defaultSupplyShareType.current) {
      form.setFieldsValue({
        supplyFee: defaultSupplyFee.current,
        supplySale: defaultSupplySale.current,
        supplyShareType: newType,
      });
    } else {
      form.setFieldsValue({
        supplyFee: 0,
        supplySale: 0,
        supplyShareType: newType,
      });
    }
  };

  return (
    <SalaryTabStyled className={v2 ? 'v2' : ''}>
      <Form
        onFinish={onFinish}
        initialValues={initSalaryInfo}
        autoComplete="off"
        layout="vertical"
        form={form}
      >
        <Form.Item name={'salaryType'} label={'Salary Method'}>
          <Radio.Group>
            <Radio value={ESalaryMode.COMMISSION}>By Commission</Radio>
            <Radio value={ESalaryMode.HOURS}>By Hourly</Radio>
            <Radio value={ESalaryMode.DAYS}>By Daily</Radio>
            <Radio value={ESalaryMode.COMMISSION_ACCELERATE}>
              Accelerate Commission 
            </Radio>
            <Radio value={ESalaryMode.FIX_SALARY}>Based Commission</Radio>
          </Radio.Group>
        </Form.Item>
        {salaryMode === ESalaryMode.FIX_SALARY ? (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="amountSalary"
                  label={<>Amount <Text color="error_6" pl={0.3}>*</Text></>}
                  rules={[{ required: true, message: 'Missing amount' }]}
                >
                  <CurrencyInputPrecision height={'3rem'} />
                </Form.Item>
              </Col>
              {/* <Col span={6}>
                <Form.Item
                  name="perTypeCash"
                  label="% Cash"
                  initialValue={0}
                >
                  <InputNumberStyled
                    min={0}
                    max={100}
                    formatter={(v: any) => `${v}%`}
                    parser={(v: any) => (v ? v.replace('%', '') : '')}
                    onChange={handlePerTypeCashChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="perTypeCheck"
                  label="% Check"
                  initialValue={100}
                >
                  <InputNumberStyled
                    min={0}
                    max={100}
                    formatter={(v: any) => `${v}%`}
                    parser={(v: any) => (v ? v.replace('%', '') : '')}
                    onChange={handlePerTypeCheckChange}
                  />
                </Form.Item>
              </Col> */}
            </Row>
            <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Text variant="H7" color="text_3">
                  Salary Per Types
                </Text>
              </HeaderItemBySalaryMode>
            <SalaryPerTypes />
            {/* <Box>
              <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Text variant="H7" color="text_3">
                  Supply Share (Exp: Salon receives $1 for every $100 the staff earns)
                </Text>
              </HeaderItemBySalaryMode>
              <BodyItemBySalaryMode className='BodyItemBySalaryMode'>
                <Form.Item name="supplyShareType" initialValue="MONEY" style={{ marginBottom: 8 }}>
                  <Radio.Group onChange={handleSupplyShareTypeChange}>
                    <Radio value="MONEY">Dollar</Radio>
                    <Radio value="PERCENT">Percent</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item shouldUpdate={(prev, curr) => prev.supplyShareType !== curr.supplyShareType} noStyle>
                  {({ getFieldValue }) => getFieldValue('supplyShareType') === 'MONEY' ? (
                    <Row gutter={16}>
                      <Col flex={1}>
                        <Form.Item
                          name="supplySale"
                          label="Sale Service"
                        >
                          <CurrencyInputPrecision height={'3rem'} />
                        </Form.Item>
                      </Col>
                      <Col flex={1}>
                        <Form.Item
                          name="supplyFee"
                          label={<>Fee <Text color="error_6" pl={0.3}>*</Text></>}
                        >
                          <CurrencyInputPrecision height={'3rem'} />
                        </Form.Item>
                      </Col>
                    </Row>
                  ) : (
                    <Form.Item
                      name="supplyFee"
                      label={<>Fee <Text color="error_6" pl={0.3}>*</Text></>}
                      rules={[{ required: true, message: 'Missing fee' }]}
                    >
                      <InputNumberStyled min={0} max={100} formatter={(v: any) => `${v || ''}%`} parser={(v: any) => (v ? v.replace('%', '') : '')} placeholder="0%" />
                    </Form.Item>
                  )}
                </Form.Item>
              </BodyItemBySalaryMode>
            </Box> */}
            <Box mt="3" style={{marginTop: '0px'}}>
              <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Text variant="H7" color="text_3">
                  Payroll Tip Options
                </Text>
              </HeaderItemBySalaryMode>
              <PayrollTipOptions />
            </Box>
            <Box mt="3" style={{marginTop: '0px'}}>
              <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Row align={'middle'}>
                  <Form.Item name='enableCleaningFee' valuePropName='checked' noStyle>
                    <SwitchV2/>
                  </Form.Item>
                  <Text ml={1}/>
                  <Text variant="H7" color="text_3">
                    Cleaning Fee
                  </Text>
                </Row>
              </HeaderItemBySalaryMode>
              <CleaningFee />
            </Box>
            <Box mt="3">
              <ButtonGroup />
            </Box>
          </>
        ) : (
          <>
            {salaryMode === ESalaryMode.COMMISSION && (
              <Row align={'middle'} style={v2 ? { alignSelf: 'stretch', margin: 0 } : { width: '50%' }} gutter={[16, 16]}>
                <Row style={{ flex: 1 }} gutter={[16, 16]}>
                  <Col flex={1}>
                    <Form.Item
                      name="commission"
                      label="Commission (%)"
                      rules={[{ required: true, message: 'Missing commission' }]}
                    >
                      <InputNumberStyled
                        min={0}
                        max={100}
                        formatter={(value: any) => `${value}%`}
                        parser={(value: any) => value?.replace('%', '')}
                      />
                    </Form.Item>

                  </Col>
                  <Col flex={1}>
                    <Form.Item
                      name="minSaleByDay"
                      label="Daily Guaranteed Minimum"
                      // rules={[{ required: true, message: 'Missing daily guaranteed minimum' }]}
                    >
                      <CurrencyInputPrecision height={'3rem'} />
                    </Form.Item>

                  </Col>
                </Row>
                <CommValueLabelCol flex={1}>
                  <Form.Item
                    name="minCommValue"
                    labelCol={{ className: 'minCommValueLabel' }}
                    label={<>
                      <Form.Item noStyle name={'enableMinComm'} fieldId='checked' valuePropName='checked'>
                        <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                      </Form.Item>
                      <Text mr={2} />
                      Weekly Minimum
                    </>}
                  >
                    <CurrencyInputPrecision height={'3rem'} />
                  </Form.Item>
                </CommValueLabelCol>
              </Row>
            )}
            {salaryMode === ESalaryMode.HOURS && (
              <Box display="flex" alignItems='center' gap="3">
                <Form.Item name="hourlyValue" label="Hourly">
                  <InputNumberStyled
                    formatter={(value: any) =>
                      `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value: any) => value?.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
                <Form.Item name='maxHoursWeekly' label="Full time hour rates (h)">
                  <Input type='number' />
                </Form.Item>
                <Form.Item name='overtimeValue' label="Overtime hour rates (h)">
                  <CurrencyInputPrecision />
                </Form.Item>
              </Box>
            )}
             {salaryMode === ESalaryMode.DAYS && (
              <Form.Item name="dailyValue" label="Daily">
                <InputNumberStyled
                  formatter={(value: any) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value: any) => value?.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            )}
            {salaryMode === ESalaryMode.COMMISSION_ACCELERATE && (
              <Box>
                <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                  <Text variant="H7" color="text_3">
                    Add Roll Level Commission
                  </Text>
                </HeaderItemBySalaryMode>
                <BodyItemBySalaryMode className='BodyItemBySalaryMode'>
                  <Box display="flex" flexDirection="column" gap="3">
                    <Form.List name="salaryAccelerateOption">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <RangeSaleInput
                              field={field}
                              remove={remove}
                              key={field.key}
                            />
                          ))}
                          <Box px="2">
                            <Button
                              ntype="LIGHT_BLUE"
                              onClick={() => add()}
                              icon="addPlus"
                            >
                              Setting
                            </Button>
                          </Box>
                        </>
                      )}
                    </Form.List>
                  </Box>
                </BodyItemBySalaryMode>
              </Box>
            )}
            <Box mt="3" style={{marginTop: '0px'}}>
              <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Text variant="H7" color="text_3">
                  Salary Per Types
                </Text>
              </HeaderItemBySalaryMode>
              <SalaryPerTypes />
            </Box>
            <Box mt="3" style={{marginTop: '0px'}}>
              <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Text variant="H7" color="text_3">
                  Payroll Tip Options
                </Text>
              </HeaderItemBySalaryMode>
              <PayrollTipOptions />
            </Box>
            <Box mt="3" style={{marginTop: '0px'}}>
              <HeaderItemBySalaryMode className='HeaderItemBySalaryMode'>
                <Row align={'middle'}>
                  <Form.Item name='enableCleaningFee' valuePropName='checked' noStyle>
                    <SwitchV2/>
                  </Form.Item>
                  <Text ml={1}/>
                  <Text variant="H7" color="text_3">
                    Cleaning Fee
                  </Text>
                </Row>
              </HeaderItemBySalaryMode>
              <CleaningFee />
            </Box>
            <Box mt="3">
              <ButtonGroup />
            </Box>
          </>
        )}
      </Form>
    </SalaryTabStyled>
  );
};

const SalaryTabStyled = styled.div`
  border-radius: 5px;
  border: 1px solid ${colorTheme.line_3};
  background: #fff;
  box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.1) inset,
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 1.5rem 4rem;
  .ant-radio-checked .ant-radio-inner {
    background: linear-gradient(to bottom, #548088, #6faab6);
    border-color: #6faab6;
  }

&.v2 {
  padding: 0px;
  border: none;
  box-shadow: none;
  background: transparent;

  .HeaderItemBySalaryMode {
    background: #E5E6EB;
  }
  .BodyItemBySalaryMode, .PayrollTipOptionsStyled, .SalaryPerTypesStyled {
    background: #F6F7FC;
    box-shadow: none;
  }
}
`;

export default StaffSalary;

const HeaderItemBySalaryMode = styled.div`
  background: var(--fill-fill-2, #adadad);
  padding: 1rem;
`;
const BodyItemBySalaryMode = styled.div`
  background: var(--fill-fill-1, #f6f7fc);
  box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.1) inset,
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 1rem;
`;

const InputNumberStyled = styled(InputNumber)`
  height: 3rem;
  width: 100%;
  .ant-input-number-input {
    height: 3rem;
    padding: 0 0.75rem;
  }
`;
const CommValueLabelCol = styled(Col)`
  .minCommValueLabel {
    label {
      width: 100%;
    }
  }
`;